import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Newspaper } from "lucide-react";
import Header from "@/sections/Header";
import FooterSection from "@/sections/Footer";
import { CornerMarks, StatusLED } from "@/components/infra";
import WireSignals from "@/components/WireSignals";
import { FEATURES, REACH_REPORT, SIGNALS, SYNDICATION, WIRE_META, WIRE_QUOTES } from "@/lib/wireData";
import { openCalendly } from "@/lib/calendly";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Favicon = ({ domain }) => (
  <span
    aria-hidden
    className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-white border border-[#DADCE1] shadow-[inset_0_1px_0_rgba(255,255,255,1),0_1px_1px_rgba(15,17,23,0.04)] overflow-hidden shrink-0"
  >
    <img
      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`}
      alt=""
      loading="lazy"
      className="w-5 h-5 object-contain"
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
    />
  </span>
);

export default function WirePage() {
  useEffect(() => {
    const target = window.location.hash ? document.getElementById(window.location.hash.slice(1)) : null;
    if (target) {
      window.setTimeout(() => target.scrollIntoView({ behavior: "instant", block: "start" }), 50);
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
    const prev = document.title;
    document.title = "The DCYB Wire — Press Coverage & Industry Signals";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <main data-testid="wire-page" className="relative bg-[#F4F5F7]">
      <Header />

      <div className="border-b hairline bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-11 md:pt-14 pb-6">
          <Link
            to="/"
            data-testid="back-to-home-link"
            className="inline-flex items-center gap-2 font-mono-label text-[11px] text-[#5F6168] hover:text-[#0F1117] transition-colors"
          >
            <ArrowLeft size={14} />
            Back to DCYB
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="border-b hairline bg-white relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-32 right-[-140px] w-[620px] h-[620px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(15,191,119,0.10), transparent 62%)" }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20 relative grid grid-cols-12 gap-y-12 gap-x-0 sm:gap-x-8 lg:gap-x-12 items-center">
          <div className="col-span-12 lg:col-span-7">
          <div className="font-mono-label text-[11px] text-[#0AAE6E] mb-6 flex items-center gap-3">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full bg-[#0FBF77] opacity-60" />
              <span className="relative inline-flex h-2 w-2 laser-dot" />
            </span>
            / The DCYB Wire · Newsroom
          </div>
          <h1 data-testid="wire-headline" className="mb-7">
            <img
              src="/dcyb-wire-logo.png?v=2"
              alt="DCYB Wire"
              data-testid="wire-logo"
              className="w-full max-w-[360px] sm:max-w-[440px] md:max-w-[520px] h-auto"
            />
            <span className="sr-only">The DCYB Wire</span>
          </h1>
          <p className="font-display text-xl sm:text-2xl font-extrabold tracking-tight text-[#0F1117] leading-snug max-w-3xl mb-4">
            The next biggest news outlet for the next generation of{" "}
            <span className="text-laser">digital infrastructure.</span>
          </p>
          <p className="text-base md:text-lg text-[#3D4049] leading-relaxed max-w-2xl mb-8">
            925 outlets. 119.8M monthly reach. 106 countries. Original industry
            signals, national financial media, founder features, and the
            syndicated wire — where the buildout gets covered first.
          </p>
          <div
            data-testid="wire-meta"
            className="flex flex-wrap items-center gap-x-8 gap-y-2"
          >
            {[
              [`${WIRE_META.links}`, "Links"],
              [`${WIRE_META.stories}`, "Unique Stories"],
              [WIRE_META.updated, "Updated"],
            ].map(([value, label]) => (
              <div key={label} className="flex items-baseline gap-2">
                <span className="font-display text-2xl md:text-3xl font-extrabold text-[#0AAE6E]">
                  {value}
                </span>
                <span className="font-mono-label text-[10px] tracking-[0.18em] uppercase text-[#71747B]">
                  {label}
                </span>
              </div>
            ))}
          </div>
          </div>

          {/* Featured: latest industry signal */}
          <a
            href={`#signal-${SIGNALS[0].slug}`}
            data-testid="wire-hero-featured-signal"
            className="group col-span-12 lg:col-span-5 chrome-surface relative block p-4 md:p-5 transition-all duration-300 hover:shadow-[0_24px_60px_rgba(15,17,23,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1117]"
          >
            <CornerMarks color="#A9ADB4" />
            <div className="flex items-center justify-between gap-3 mb-4">
              <span className="inline-flex items-center gap-2.5 font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#0F1117]">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full bg-red-500 opacity-60" />
                  <span className="relative inline-flex h-2 w-2 bg-red-500" />
                </span>
                Latest · {SIGNALS[0].kicker}
              </span>
              <span className="font-mono-label text-[10px] tracking-[0.16em] uppercase text-[#71747B]">
                {SIGNALS[0].date}
              </span>
            </div>
            <div className="relative aspect-[16/10] overflow-hidden border border-[#D2D5DA] bg-white">
              <img
                src={SIGNALS[0].image.src}
                alt=""
                aria-hidden
                className="absolute inset-0 w-full h-auto object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]"
                draggable="false"
              />
              <div
                aria-hidden
                className="absolute inset-x-0 bottom-0 h-16"
                style={{ background: "linear-gradient(180deg, rgba(255,255,255,0), #FFFFFF)" }}
              />
            </div>
            <h2 className="font-display text-xl md:text-2xl font-extrabold tracking-tight leading-[1.1] text-[#0F1117] text-balance mt-5 mb-4">
              {SIGNALS[0].headline}
            </h2>
            <span className="inline-flex items-center gap-2 font-mono-label text-[10.5px] tracking-[0.16em] uppercase text-[#5F6168] group-hover:text-[#0F1117] transition-colors">
              <span className="live-dot" aria-hidden />
              Read the signal
              <ArrowUpRight
                size={13}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </span>
          </a>
        </div>
      </section>

      <WireSignals />

      {/* Feature coverage index */}
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
          <div className="font-mono-label text-[11px] text-[#0F1117] mb-8 flex items-center gap-3">
            <StatusLED />
            Feature coverage
          </div>
          <div data-testid="wire-feature-list" className="border-t border-[#D2D5DA]">
            {FEATURES.map((a) => (
              <a
                key={a.url}
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`wire-row-${slug(a.outlet)}`}
                className="group grid grid-cols-12 gap-x-4 gap-y-2 items-start py-5 md:py-6 border-b border-[#D2D5DA] bg-transparent hover:bg-white transition-all px-2 md:px-4 -mx-2 md:-mx-4 border-l-2 border-l-transparent hover:border-l-[#0FBF77] hover:shadow-[0_8px_24px_rgba(15,191,119,0.08)]"
              >
                <div className="col-span-12 md:col-span-2 font-mono-label text-[10px] tracking-[0.16em] uppercase text-[#71747B] pt-2">
                  {a.date}
                </div>
                <div className="col-span-12 md:col-span-3 flex items-center gap-3">
                  <Favicon domain={a.domain} />
                  <span className="font-display text-base md:text-lg font-extrabold tracking-tight text-[#0F1117]">
                    {a.outlet}
                  </span>
                </div>
                <div className="col-span-11 md:col-span-6">
                  <div className="text-[15px] md:text-base font-semibold text-[#0F1117] leading-snug mb-1.5">
                    {a.headline}
                  </div>
                  <div className="hidden md:block text-[13px] text-[#71747B] leading-relaxed">
                    {a.angle}
                  </div>
                </div>
                <div className="col-span-1 flex justify-end pt-1.5">
                  <ArrowUpRight
                    size={16}
                    className="text-[#71747B] transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#0FBF77]"
                  />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Wire syndication */}
      <section className="border-b hairline bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
          <div className="font-mono-label text-[11px] text-[#0F1117] mb-8 flex items-center gap-3">
            <StatusLED />
            Wire syndication
          </div>
          <div className="chrome-surface relative p-7 md:p-10 mb-6 md:mb-8">
            <CornerMarks color="#A9ADB4" />
            <div className="flex items-center gap-3 mb-4">
              <Newspaper size={13} className="text-[#0F1117]" aria-hidden />
              <span className="font-mono-label text-[10.5px] tracking-[0.16em] text-[#5F6168] uppercase">
                {SYNDICATION.date} · Global release
              </span>
            </div>
            <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight leading-[1.1] text-[#0F1117] text-balance max-w-3xl">
              {SYNDICATION.headline}.
            </h2>
          </div>
          <div
            data-testid="wire-syndication-grid"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4"
          >
            {SYNDICATION.outlets.map((o) => (
              <a
                key={o.name}
                href={o.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`wire-synd-${slug(o.name)}`}
                className="group flex flex-col items-start gap-3 bg-white p-4 md:p-5 border border-[#D2D5DA] transition-all duration-200 hover:border-[#0FBF77]/70 hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(15,191,119,0.16)]"
              >
                <Favicon domain={o.domain} />
                <span className="font-display text-sm md:text-[15px] font-extrabold tracking-tight text-[#0F1117] leading-tight">
                  {o.name}
                </span>
                <span className="inline-flex items-center gap-1.5 font-mono-label text-[9px] tracking-[0.16em] uppercase text-[#5F6168] group-hover:text-[#0AAE6E] transition-colors">
                  <span className="inline-block w-1.5 h-1.5 rounded-full laser-dot laser-pulse" />
                  Read
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Reach report */}
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
          <div className="font-mono-label text-[11px] text-[#0F1117] mb-2 flex items-center gap-3">
            <StatusLED />
            Reach report
          </div>
          <p className="text-[13px] text-[#71747B] mb-8">
            Verified syndication analytics for the {REACH_REPORT.date} release.
          </p>

          <div
            data-testid="wire-reach-stats"
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-10"
          >
            {[
              [REACH_REPORT.placements, "Outlet Placements"],
              [REACH_REPORT.monthlyReach, "Unique Monthly Reach"],
              [REACH_REPORT.monthlyVisits, "Monthly Visits Across Outlets"],
              [REACH_REPORT.countries, "Countries"],
            ].map(([value, label]) => (
              <div key={label} className="chrome-surface relative p-4 sm:p-5 md:p-7 min-w-0">
                <CornerMarks color="#A9ADB4" />
                <div className="font-display text-2xl sm:text-3xl md:text-5xl font-extrabold tracking-tight text-[#0AAE6E] mb-2">
                  {value}
                </div>
                <div className="font-mono-label text-[9.5px] tracking-[0.18em] uppercase text-[#71747B]">
                  {label}
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Region distribution */}
            <div className="lg:col-span-5">
              <div className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#5F6168] mb-5">
                Placements by region
              </div>
              <div data-testid="wire-region-bars" className="space-y-4">
                {REACH_REPORT.regions.map((r) => (
                  <div key={r.name}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-mono-label text-[10px] tracking-[0.16em] uppercase text-[#0F1117]">
                        {r.name}
                      </span>
                      <span className="font-mono-label text-[10px] tracking-[0.16em] text-[#71747B]">
                        {r.count}
                      </span>
                    </div>
                    <div className="h-[6px] bg-[#E2E4E9] overflow-hidden">
                      <div
                        className="h-full"
                        style={{
                          width: `${(r.count / REACH_REPORT.regions[0].count) * 100}%`,
                          background: "linear-gradient(90deg, #0AAE6E, #0FBF77)",
                          boxShadow: "0 0 10px rgba(15,191,119,0.5)",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top outlets by audience */}
            <div className="lg:col-span-7">
              <div className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#5F6168] mb-5">
                Top placements by monthly audience
              </div>
              <div data-testid="wire-top-outlets" className="border-t border-[#D2D5DA]">
                {REACH_REPORT.topOutlets.map((o) => (
                  <a
                    key={o.name}
                    href={o.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`wire-top-${slug(o.name)}`}
                    className="group flex items-center gap-4 py-3.5 border-b border-[#D2D5DA] hover:bg-white transition-colors px-2 -mx-2"
                  >
                    <Favicon domain={o.domain} />
                    <span className="flex-1 font-display text-[15px] md:text-base font-extrabold tracking-tight text-[#0F1117]">
                      {o.name}
                    </span>
                    <span className="font-mono-label text-[10px] tracking-[0.14em] uppercase text-[#71747B] hidden sm:inline">
                      {o.country}
                    </span>
                    <span className="font-display text-base md:text-lg font-extrabold text-[#0F1117] w-16 text-right">
                      {o.reach}
                    </span>
                    <ArrowUpRight
                      size={14}
                      className="text-[#71747B] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#0F1117]"
                    />
                  </a>
                ))}
              </div>
              <p className="mt-4 font-mono-label text-[9px] tracking-[0.16em] uppercase text-[#8A8D94]">
                Unique visitors per month · GlobeNewswire syndication report
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quote bank */}
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
          <div className="font-mono-label text-[11px] text-[#0F1117] mb-8 flex items-center gap-3">
            <StatusLED />
            From the coverage
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {WIRE_QUOTES.map((q, i) => (
              <figure
                key={q.source + i}
                data-testid={`wire-quote-${i}`}
                className="chrome-surface relative p-6 md:p-8 border-t-2 border-t-[#0FBF77]"
              >
                <CornerMarks color="#A9ADB4" />
                <div className="font-display text-4xl font-extrabold text-[#0FBF77] leading-none mb-3 select-none" aria-hidden>
                  “
                </div>
                <blockquote className="font-display text-lg md:text-xl font-extrabold tracking-tight leading-snug text-[#0F1117] mb-5">
                  {q.quote}
                </blockquote>
                <figcaption className="font-mono-label text-[10px] tracking-[0.18em] uppercase text-[#0AAE6E]">
                  — {q.source}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Press CTA */}
      <section className="border-b hairline bg-white relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -bottom-40 right-[-100px] w-[480px] h-[480px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(15,191,119,0.10), transparent 60%)" }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10 relative">
          <div className="flex-1">
            <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-[#0F1117] mb-2">
              Covering the workforce behind the{" "}
              <span className="text-laser">buildout?</span>
            </h2>
            <p className="text-[15px] text-[#52555B]">
              Press inquiries and interview requests — Luke answers fast.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href="mailto:luke@dcyb.ai"
              data-testid="wire-press-email-btn"
              className="chrome-btn inline-flex items-center gap-2 font-mono-label text-[11px] tracking-[0.14em] px-6 py-3.5"
            >
              luke@dcyb.ai
              <ArrowUpRight size={14} />
            </a>
            <button
              type="button"
              onClick={openCalendly}
              data-testid="wire-book-btn"
              className="laser-btn inline-flex items-center gap-2 font-mono-label text-[11px] tracking-[0.14em] px-6 py-3.5"
            >
              Book a Meeting
              <ArrowUpRight size={14} />
            </button>
          </div>
        </div>
      </section>

      <FooterSection />
    </main>
  );
}
